import { DASHBOARD_URL, PAYPAL_AUTHORIZE_URL, PAYPAL_CLIENT_ID } from '@/env'

// PayPal OAuth withdraw "ceremony" — a single-withdraw, session-scoped handshake.
// Nothing here persists between withdraws: the stash is created when the user starts the
// consent redirect, consumed when the modal resumes after the callback, and never outlives
// its TTL (PC2, IV5).

const SESSION_STORAGE_KEY_STASH = 'dh:paypal-ceremony'
const STASH_TTL_MS = 10 * 60 * 1000 // 10 minutes

export type CeremonyStash = {
  amountUsd: string
  stateNonce: string
  idempotencyKey: string
  createdAt: number
}

/** Location.state key flagging that the user cancelled the PayPal consent screen. */
export const PAYPAL_CONSENT_CANCELLED_STATE = 'paypalConsentCancelled'

const readStash = (): CeremonyStash | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY_STASH)
    return raw ? (JSON.parse(raw) as CeremonyStash) : null
  } catch (err) {
    console.warn('Failed to read PayPal ceremony stash from sessionStorage', err)
    return null
  }
}

/**
 * Creates a fresh ceremony stash for one withdraw attempt and persists it to sessionStorage
 * so it survives the PayPal consent redirect. `stateNonce` (CSRF state) and `idempotencyKey`
 * are independent random UUIDs.
 *
 * Throws when sessionStorage rejects the write: a redirect without a persisted stash would
 * dead-end at "start over" after consent — the caller must not start the ceremony.
 */
export const createStash = (amountUsd: string): CeremonyStash => {
  const stash: CeremonyStash = {
    amountUsd,
    stateNonce: crypto.randomUUID(),
    idempotencyKey: crypto.randomUUID(),
    createdAt: Date.now()
  }
  window.sessionStorage.setItem(SESSION_STORAGE_KEY_STASH, JSON.stringify(stash))
  return stash
}

/**
 * Returns the stash if present and fresh (within TTL). A stale stash is removed and
 * treated as absent (IV5). Does not clear a fresh stash.
 */
export const peekFreshStash = (): CeremonyStash | null => {
  const stash = readStash()
  if (!stash) return null
  if (Date.now() - stash.createdAt > STASH_TTL_MS) {
    clearStash()
    return null
  }
  return stash
}

/**
 * Returns the fresh stash (if any) and clears it — used when the withdraw modal resumes
 * after the consent redirect (IV5).
 */
export const consumeStash = (): CeremonyStash | null => {
  const stash = peekFreshStash()
  if (stash) clearStash()
  return stash
}

export const clearStash = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY_STASH)
  } catch (err) {
    console.warn('Failed to clear PayPal ceremony stash from sessionStorage', err)
  }
}

/** Builds the PayPal consent (authorize) URL for the given CSRF state nonce. */
export const buildPayPalAuthorizeUrl = (stateNonce: string): string => {
  const params = new URLSearchParams({
    flowEntry: 'static',
    client_id: PAYPAL_CLIENT_ID,
    response_type: 'code',
    scope: 'openid email https://uri.paypal.com/services/paypalattributes',
    redirect_uri: `${DASHBOARD_URL}/auth/paypal`,
    state: stateNonce
  })
  return `${PAYPAL_AUTHORIZE_URL}?${params.toString()}`
}

const PAYPAL_CALLBACK_PATH = '/auth/paypal'

type PayPalCallbackParams = { code?: string; state?: string; error?: string }

// Module memory only — the authorization code must never touch storage, analytics, or Sentry.
let callbackParams: PayPalCallbackParams | null = null

/**
 * Boot-time capture of the PayPal consent callback (clones the `captureUtmParams` idiom).
 * If the app loads on `/auth/paypal` with query params, copies `code`/`state`/`error` into
 * module memory and immediately strips the query via `history.replaceState` — this must run
 * before any SDK init so PostHog's pageview capture and Sentry's pageload tracing never see
 * the URL with the authorization code (IV3).
 */
export const capturePayPalCallbackParams = (): void => {
  if (typeof window === 'undefined') return
  if (window.location.pathname !== PAYPAL_CALLBACK_PATH) return
  if (!window.location.search) return
  const searchParams = new URLSearchParams(window.location.search)
  const captured: PayPalCallbackParams = {}
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const error = searchParams.get('error')
  if (code) captured.code = code
  if (state) captured.state = state
  if (error) captured.error = error
  // A query with none of the OAuth params is not a consent callback — capture nothing and
  // leave the URL alone (the reader's contract is "params or null").
  if (!captured.code && !captured.state && !captured.error) return
  callbackParams = captured
  window.history.replaceState(window.history.state, '', PAYPAL_CALLBACK_PATH)
}

/** Returns the params captured at boot by `capturePayPalCallbackParams`, or null if none. */
export const readPayPalCallbackParams = (): PayPalCallbackParams | null => {
  return callbackParams
}
